import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Upload, 
  X, 
  Star, 
  Image as ImageIcon, 
  Video, 
  Trash2,
  Eye,
  AlertCircle
} from "lucide-react";
import { PropertyMedia } from "@/mockData/rooms";

interface MediaUploadProps {
  media: PropertyMedia[];
  onMediaChange: (media: PropertyMedia[]) => void;
  onFilesChange?: (files: File[]) => void;
  propertyId?: string;
}

export function MediaUpload({ media, onMediaChange, onFilesChange, propertyId }: MediaUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [actualFiles, setActualFiles] = useState<File[]>([]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles = Array.from(files).filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type.startsWith('video/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      return isValidType && isValidSize;
    });

    if (validFiles.length === 0) {
      alert(t("supportedFormats") + "\n" + t("maxFileSize"));
      return;
    }

    setUploading(true);
    
    // Simulate file upload
    setTimeout(() => {
      const newMedia: PropertyMedia[] = validFiles.map((file, index) => ({
        id: `temp_${Date.now()}_${index}`,
        property_id: propertyId || 'temp',
        url: URL.createObjectURL(file),
        type: file.type.startsWith('image/') ? 'image' : 'video',
        alt_text: file.name,
        title: file.name,
        description: '',
        is_primary: media.length === 0 && index === 0, // First file becomes primary if no media exists
        created_at: new Date().toISOString()
      }));

      onMediaChange([...media, ...newMedia]);
      
      // Update actual files for API upload
      const updatedFiles = [...actualFiles, ...validFiles];
      setActualFiles(updatedFiles);
      if (onFilesChange) {
        onFilesChange(updatedFiles);
      }
      
      setUploading(false);
    }, 1000);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeMedia = (mediaId: string) => {
    const mediaIndex = media.findIndex(m => m.id === mediaId);
    const updatedMedia = media.filter(m => m.id !== mediaId);
    
    // If we removed the primary image, make the first remaining image primary
    if (media.find(m => m.id === mediaId)?.is_primary && updatedMedia.length > 0) {
      updatedMedia[0].is_primary = true;
    }
    onMediaChange(updatedMedia);
    
    // Also remove from actual files if it's a new upload
    if (mediaIndex >= 0 && mediaIndex < actualFiles.length) {
      const updatedFiles = actualFiles.filter((_, index) => index !== mediaIndex);
      setActualFiles(updatedFiles);
      if (onFilesChange) {
        onFilesChange(updatedFiles);
      }
    }
  };

  const setPrimary = (mediaId: string) => {
    const updatedMedia = media.map(m => ({
      ...m,
      is_primary: m.id === mediaId
    }));
    onMediaChange(updatedMedia);
  };

  const getFileIcon = (type: string) => {
    return type === 'image' ? ImageIcon : Video;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          {t("mediaFiles")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragOver 
              ? 'border-primary bg-primary/5' 
              : 'border-muted-foreground/25 hover:border-primary/50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-medium mb-2">{t("dragDropFiles")}</p>
          <p className="text-sm text-muted-foreground mb-4">
            {t("supportedFormats")}
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            {t("maxFileSize")}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? t("loading") : t("selectFiles")}
          </Button>
          <Input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>

        {/* Media Grid */}
        {media.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {media.map((item) => {
              const FileIcon = getFileIcon(item.type);
              return (
                <Card key={item.id} className="relative group">
                  <CardContent className="p-2">
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                      {item.type === 'image' ? (
                        <img
                          src={item.url}
                          alt={item.alt_text}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}
                      
                      {/* Primary Badge */}
                      {item.is_primary && (
                        <Badge className="absolute top-2 left-2 bg-primary">
                          <Star className="h-3 w-3 mr-1" />
                          {t("primaryImage")}
                        </Badge>
                      )}

                      {/* Action Buttons */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setPrimary(item.id)}
                          disabled={item.is_primary}
                        >
                          <Star className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => window.open(item.url, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => removeMedia(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Media Info */}
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <FileIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium truncate">
                          {item.title}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.alt_text}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Media Details Form */}
        {media.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-medium">{t("mediaDetails")}</h4>
            {media.map((item, index) => (
              <Card key={item.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      {item.type === 'image' ? (
                        <img
                          src={item.url}
                          alt={item.alt_text}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor={`title_${item.id}`}>{t("mediaTitle")}</Label>
                          <Input
                            id={`title_${item.id}`}
                            value={item.title}
                            onChange={(e) => {
                              const updatedMedia = media.map(m => 
                                m.id === item.id ? { ...m, title: e.target.value } : m
                              );
                              onMediaChange(updatedMedia);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`alt_${item.id}`}>{t("altText")}</Label>
                          <Input
                            id={`alt_${item.id}`}
                            value={item.alt_text}
                            onChange={(e) => {
                              const updatedMedia = media.map(m => 
                                m.id === item.id ? { ...m, alt_text: e.target.value } : m
                              );
                              onMediaChange(updatedMedia);
                            }}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`desc_${item.id}`}>{t("mediaDescription")}</Label>
                        <Textarea
                          id={`desc_${item.id}`}
                          value={item.description}
                          onChange={(e) => {
                            const updatedMedia = media.map(m => 
                              m.id === item.id ? { ...m, description: e.target.value } : m
                            );
                            onMediaChange(updatedMedia);
                          }}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
